
import { Link } from 'react-router-dom';
import Footer from '../../components/feature/Footer';
import Navbar from '../../components/feature/Navbar';
import { getAboutUs, getStatistics } from '../../api/apiServices';
import { useEffect, useState } from 'react';

export default function AboutPage() {
  const [about, setAbout] = useState(null);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchAbout(), fetchStats()]).finally(() =>
      setLoading(false)
    );
  }, []);

  const fetchAbout = async () => {
    try {
      const res = await getAboutUs();
      if (res?.data?.isPublished) {
        setAbout(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch about info", err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await getStatistics();
      setStats(res.data || []);
    } catch (err) {
      console.error("Failed to fetch statistics", err);
    }
  };

  if (loading) {
    return <p className="text-center py-20 text-gray-500">Loading…</p>;
  }

  if (!about) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        About information not available
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-20 sm:pt-24">
        <div className="relative h-[400px] sm:h-[500px]">
          <img
            src={about.image}
            alt={about.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#6a4bc3]/90 to-[#6a4bc3]/70" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-20">
              <p className="text-white/80 text-sm mb-3">OUR STORY</p>
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-6">
                {about.title}
              </h1>
              <p className="text-white/90 text-base sm:text-lg max-w-2xl">
                {about.description}
              </p>
            </div>
          </div>
        </div>
      </section>

     {/* MISSION */}
<section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-20 bg-[#faf8f2]">
  <div className="max-w-6xl mx-auto">

    {/* Header */}
    <div className="max-w-3xl mb-14">
      <p className="text-[#c9a24d] text-sm font-semibold tracking-widest uppercase mb-3">
        Our Mission
      </p>

      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
        Restoring Hope, Rebuilding Lives
      </h2>
    </div>

    {about?.mission && (() => {

      // Split blocks by empty lines
      const parts = about.mission.trim().split(/\n\s*\n/);

      const intro = parts[0];

      const numberedItems = [];
      const closingParts = [];

      // Separate numbered sections from closing text
      parts.slice(1).forEach((block) => {
        if (/^\d+\./.test(block.trim())) {
          numberedItems.push(block);
        } else {
          closingParts.push(block);
        }
      });

      // Join all non-numbered ending blocks
      const closingText = closingParts.join("\n\n");

      // Detect signature (last 1–2 short lines)
      let closingParagraph = closingText;
      let signatureName = null;
      let signatureRole = null;

      if (closingText) {
        const lines = closingText.split("\n").map(l => l.trim()).filter(Boolean);

        if (lines.length >= 2 && lines[lines.length - 1].length < 40) {
          signatureRole = lines.pop();
          signatureName = lines.pop();
          closingParagraph = lines.join("\n");
        }
      }

      return (
        <>
          {/* Intro Paragraph */}
          <p className="text-gray-700 leading-relaxed text-lg max-w-3xl mb-12 whitespace-pre-line">
            {intro}
          </p>

          {/* Mission Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {numberedItems.map((item, index) => {
              const lines = item.split("\n");
              const title = lines[0].replace(/^\d+\.\s*/, "");
              const desc = lines.slice(1).join(" ");

              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition"
                >
                  <div className="flex items-start gap-4">
                    <div className="min-w-[42px] h-[42px] flex items-center justify-center rounded-full bg-[#c9a24d]/10 text-[#c9a24d] font-bold">
                      {index + 1}
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {title}
                      </h3>

                      <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                        {desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Closing Statement */}
          {closingParagraph && (
            <div className="mt-16 max-w-3xl">
              <p className="text-gray-700 leading-relaxed mb-6 whitespace-pre-line">
                {closingParagraph}
              </p>

              {/* Signature */}
              {signatureName && (
                <div className="border-l-4 border-[#c9a24d] pl-4 italic text-gray-800">
                  <p className="font-semibold">{signatureName}</p>
                  {signatureRole && (
                    <p className="text-sm text-gray-500">{signatureRole}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      );
    })()}
  </div>
</section>



      {/* VISION */}
{about.vision && (
  <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-20 bg-gray-50">
    <div className="max-w-5xl mx-auto">
      <p className="text-[#c9a24d] text-sm font-semibold mb-3">
        OUR VISION
      </p>
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
        A Future Filled With Hope
      </h2>

      {about.vision
        .split("\n\n")
        .map((paragraph, index) => (
          <p
            key={index}
            className="text-gray-700 leading-relaxed mb-6"
          >
            {paragraph}
          </p>
        ))}
    </div>
  </section>
)}

{/* MEET THE FOUNDER */}
{about?.founder && (
  <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-20 bg-white">
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-2 lg:gap-10 items-center bg-gray-50 rounded-3xl p-6 sm:p-8 lg:p-12">
        
        {/* IMAGE */}
        <div className="flex justify-center lg:justify-start">
          <img
            src={about.founder.image}
            alt={about.founder.name}
            className="w-64 sm:w-72 h-80 object-cover rounded-xl shadow-md"
          />
        </div>

        {/* CONTENT */}
        <div>
          <p className="text-[#c9a24d] text-sm font-semibold mb-2">
            MEET THE FOUNDER
          </p>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {about.founder.name}
          </h2>

          {/* QUOTE */}
          <blockquote className="relative pl-5 mb-5 text-gray-700 italic">
            <span className="absolute left-0 top-0 h-full w-1 bg-[#c9a24d] rounded-full"></span>
            {about?.founder?.quote}
          </blockquote>

          {/* BIO */}
          <div className="space-y-4">
            {about.founder.bio
              ?.split("\n\n")
              .map((paragraph, index) => (
                <p
                  key={index}
                  className="text-gray-700 leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}
          </div>
        </div>
      </div>
    </div>
  </section>
)}



      {/* STATISTICS */}
      {stats.length > 0 && (
        <section className="bg-[#6a4bc3] py-16 sm:py-20 px-4 sm:px-6 lg:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {stats.map((stat) => (
                <div key={stat._id} className="text-center">
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#c9a24d] mb-2">
                    {stat.number}
                  </div>
                  <div className="text-white text-sm sm:text-base">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

